import { useCallback, useEffect, useState } from 'react'
import { 
    useDeleteProjectMutation, 
    useMoveToTrashbinMutation 
} from '@hooks/index';
import { useContextAwareLazyGetProjectById } from '@hooks/index';
import { EventTypes, useFirebaseHook } from '@utils/firebaseUtils';
import { FastProject } from '@typedefs/interfaces';
import useErrorMessage from '@utils/useErrorMessage';

const useDeleteProject = (refetch?: () => void) => {
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [toDeleteId, setToDeleteId] = useState<number | null>(null);
    const [isDeleteDialogErrorMessageOpen, setIsDeleteDialogErrorMessageOpen] = useState(false);
    const { fireEvent } = useFirebaseHook();
    const [getProjectById] = useContextAwareLazyGetProjectById();

    const [deleteProject, { isLoading: isLoadingDelete, isError, error }] = useDeleteProjectMutation();
    const [moveToTrashbin, { isLoading: isLoadingMoveToTrashbin, isError: isErrorMoveToTrashbin, error: errorMoveToTrashbin }] = useMoveToTrashbinMutation();

    const { errorMsg } = useErrorMessage({ isError, error });
    const { errorMsg: errorMsgMoveToTrashbin } = useErrorMessage({ isError: isErrorMoveToTrashbin, error: errorMoveToTrashbin });

    useEffect(() => {
        if (errorMsg)
            setIsDeleteDialogErrorMessageOpen(true);
        else if (errorMsgMoveToTrashbin)
            setIsDeleteDialogErrorMessageOpen(true);
    }, [errorMsg, errorMsgMoveToTrashbin]);

    const closeDeleteDialogErrorMessage = useCallback(() => {
        setIsDeleteDialogErrorMessageOpen(false);
    }, []);

    const handleDelete = async () => {
        if (isLoadingDelete || isLoadingMoveToTrashbin || !toDeleteId) return;
        try {
            const project = await getProjectById(`${toDeleteId}`).unwrap();
            !project.is_archived
                ? await moveToTrashbin({ id: toDeleteId }).unwrap()
                : await deleteProject(toDeleteId).unwrap();
            fireEvent(EventTypes.delete_project)
            if (refetch) refetch();
        } catch (error) {
            console.error('Failed to delete project:', error);
        } finally {
            setDeleteDialogOpen(false);
        }
    }

    const handleRestore = async (id: number) => {
        if (isLoadingDelete || isLoadingMoveToTrashbin) return;
        try {
            await moveToTrashbin({ id, restore: true }).unwrap();
            if (refetch) refetch();
        } catch (error) {
            console.error('Failed to restore project:', error);
        }
    }

    const showDeleteModal = (project: FastProject) => {
        setDeleteDialogOpen(true)
        setToDeleteId(project.id)
    }

    const handleCancel = () => {
        setDeleteDialogOpen(false)
    }

    return {
        isDeleteDialogOpen,
        handleCancel,
        handleDelete,
        showDeleteModal,
        isLoadingDelete: isLoadingDelete || isLoadingMoveToTrashbin,
        deleteProjectErrorMsg: errorMsgMoveToTrashbin ? errorMsgMoveToTrashbin : errorMsg,
        isDeleteDialogErrorMessageOpen,
        closeDeleteDialogErrorMessage,
        handleRestore
    }

}

export default useDeleteProject